import { useState, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { authApi } from "../api/auth";
import { login as loginRequest } from "../auth/auth";

/**
 * useGuestAccountCreation
 *
 * Shared logic for the inline "create a free account" opt-in offered to guests
 * during checkout (ticket purchase + lesson booking). It owns:
 *   - the opt-in + password state
 *   - a submit-time register → login → set-context sequence
 *
 * It deliberately does NOT own the email field or the has-account gating — those
 * differ per flow (booking has a guest-shadow reconcile branch tickets don't), so
 * each page runs its own email check and tells this hook whether the email is a
 * known account. The page decides WHEN to call createAccountIfRequested() in its
 * own submit sequence (tickets: before the payment intent; booking: before
 * createBooking) so the purchase/booking completes as an authenticated user.
 *
 * Login is email-only and username is auto-generated server-side, so no username
 * is sent — first/last/email/password is the whole payload.
 *
 * @returns {{
 *   wantsAccount: boolean,
 *   setWantsAccount: (v: boolean) => void,
 *   password: string,
 *   setPassword: (v: string) => void,
 *   createdAccountThisSession: boolean,
 *   passwordError: string,
 *   setPasswordError: (v: string) => void,
 *   reset: () => void,
 *   validatePassword: () => boolean,
 *   createAccountIfRequested: (args: { firstName: string, lastName: string, email: string }) => Promise<boolean>,
 * }}
 */
export default function useGuestAccountCreation() {
    const { login: setUserInContext } = useAuth();

    const [wantsAccount, setWantsAccount] = useState(false);
    const [password, setPassword] = useState("");
    const [passwordError, setPasswordError] = useState("");
    // True once an account was created via the opt-in this session — lets the
    // success screen keep pitching the app to a just-created user.
    const [createdAccountThisSession, setCreatedAccountThisSession] = useState(false);

    const reset = useCallback(() => {
        setWantsAccount(false);
        setPassword("");
        setPasswordError("");
    }, []);

    // Validate the password field. Only meaningful when the opt-in is checked.
    // Returns true when OK (or not opted in); sets passwordError and returns false otherwise.
    const validatePassword = useCallback(() => {
        if (!wantsAccount) {
            setPasswordError("");
            return true;
        }
        if (!password) {
            setPasswordError("Choose a password to create your account");
            return false;
        }
        if (password.length < 5) {
            setPasswordError("Password must be at least 5 characters");
            return false;
        }
        setPasswordError("");
        return true;
    }, [wantsAccount, password]);

    /**
     * Register + log in the guest so tokens land in localStorage + context, making
     * the ongoing checkout complete as an authenticated user. Call this only after
     * the page has confirmed the email is NEW (no existing account). Returns true on
     * success; on failure sets passwordError and returns false.
     */
    const createAccountIfRequested = useCallback(async ({ firstName, lastName, email }) => {
        const trimmedEmail = (email || "").trim();
        try {
            const registerResult = await authApi.register({
                firstName: (firstName || "").trim(),
                lastName: (lastName || "").trim(),
                email: trimmedEmail,
                password,
            });
            if (!registerResult?.success) {
                throw new Error(registerResult?.message || "Registration failed");
            }

            const loginResult = await loginRequest(trimmedEmail, password);
            if (!loginResult?.success) {
                throw new Error(loginResult?.message || "Account created, but sign-in failed");
            }
            localStorage.setItem("accessToken", loginResult.accessToken);
            localStorage.setItem("refreshToken", loginResult.refreshToken);
            setUserInContext({ ...loginResult.user, accountDetails: loginResult.accountDetails });
            setCreatedAccountThisSession(true);
            return true;
        } catch (err) {
            setPasswordError(err?.message || "Could not create your account. Please try again.");
            return false;
        }
    }, [password, setUserInContext]);

    return {
        wantsAccount,
        setWantsAccount,
        password,
        setPassword,
        createdAccountThisSession,
        passwordError,
        setPasswordError,
        reset,
        validatePassword,
        createAccountIfRequested,
    };
}
